// apps/web/src/features/crm/leads/card/leadStagePath.ts
//
// Pure funnel → `StagePathStage[]` mapping for the card's stage rail (#2308,
// #2247 FR-3, INV-1). Steps are the four linear funnel positions
// (new → inWork → closing → deal). `deal` is NEVER manually actionable — it is
// `current` only in the DEAL phase, otherwise `muted`, never `available`. The
// only actionable pill is the immediate next step that carries a real
// transition target and is manager-actionable; the `inWork` step is `blocked`
// (never `available`) while the lead is unbound, mirroring the domain gate
// (an unbound IN_WORK is rejected server-side). Kept free of `t`/JSX so labels
// and the blocked reason arrive via args (i18next typed-key contract).
import { EngagementPhase, EngagementStage, type LeadStageCode } from '@magimp/shared';
import type { StagePathStage } from '../../../../ui/index.js';

type StepKey = 'new' | 'inWork' | 'closing' | 'deal';

interface StepDef {
  key: StepKey;
  /** `null` for steps with no manual transition target (`new`, `deal`). */
  toStage: LeadStageCode | null;
  /** IN_WORK requires a bound client (INV-1). */
  clientGated: boolean;
}

const STEPS: readonly StepDef[] = [
  { key: 'new', toStage: null, clientGated: false },
  { key: 'inWork', toStage: 'in_work', clientGated: true },
  { key: 'closing', toStage: 'awaiting_payment', clientGated: false },
  { key: 'deal', toStage: null, clientGated: false },
];

export interface LeadStagePathArgs {
  phase: EngagementPhase;
  stage: EngagementStage;
  hasClient: boolean;
  canManage: boolean;
  labels: Record<StepKey, string>;
  /** Tooltip shown on the blocked `inWork` step while the lead is unbound. */
  inWorkLockedReason: string;
  run: (toStage: LeadStageCode) => void;
}

function currentIndex(phase: EngagementPhase, stage: EngagementStage): number {
  if (phase === EngagementPhase.DEAL) return 3;
  if (phase === EngagementPhase.CLOSING) return 2;
  if (phase === EngagementPhase.LEAD && stage === EngagementStage.IN_WORK) return 1;
  return 0; // NEW / POSTPONED
}

export function leadStagePathStages(args: LeadStagePathArgs): StagePathStage[] {
  const current = currentIndex(args.phase, args.stage);
  return STEPS.map((step, i): StagePathStage => {
    const label = args.labels[step.key];
    if (i < current) return { key: step.key, label, state: 'done' };
    if (i === current) return { key: step.key, label, state: 'current' };

    // Future step: only the immediate next step with a real, manager-actionable
    // target is interactive; everything else is muted.
    const actionable = args.canManage && i === current + 1 && step.toStage !== null;
    if (!actionable) return { key: step.key, label, state: 'muted' };

    if (step.clientGated && !args.hasClient) {
      return { key: step.key, label, state: 'blocked', blockedReason: args.inWorkLockedReason };
    }
    const toStage = step.toStage as LeadStageCode;
    return { key: step.key, label, state: 'available', onSelect: () => args.run(toStage) };
  });
}
